
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import AppFooter from '../AppFooter'
import { FaPlus, FaHome, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { canAddUser as checkCanAddUserApi, registerUser as registerUserApi } from '../../services/tenantUsersApi';
import { getSessionLocal } from '../../services/sessionLocal';
import { can } from '../../services/rolePermissions';
import PasswordInput from '../shared/PasswordInput';

function UserAdd() {
  const { t } = useTranslation();
  const { tenant } = useParams(); // Extract tenantId from the URL
  const session = getSessionLocal();
  const hasAddPermission = can(session.role, 'tenantUsers', 'add');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  // ⭐ New: Password confirmation state
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('manager');
  // ⭐ New: Password match validation state
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');

  // ⭐ New state for user limit checking
  const [userLimitInfo, setUserLimitInfo] = useState(null);
  const [canAddUser, setCanAddUser] = useState(true);
  const [isCheckingLimit, setIsCheckingLimit] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const notifySuccess = (message) => toast.success(message);
  const notifyError = (message) => toast.error(message);

  // ⭐ New: Check user limit when component mounts
  useEffect(() => {
    checkUserLimit();
  }, [tenant]);

  /**
   * ⭐ New function: Check if tenant can add more users
   */
  const checkUserLimit = async () => {
    setIsCheckingLimit(true);
    try {
      const data = await checkCanAddUserApi(tenant);

      if (data.success) {
        setUserLimitInfo({
          currentUsers: data.currentUsers,
          maxUsers: data.maxUsers,
          message: data.message
        });
        setCanAddUser(data.canAdd);

        if (!data.canAdd) {
          notifyError(data.message);
        }
      }
    } catch (error) {
      console.error('Error checking user limit:', error);
      // On error, allow the form (fail open for better UX)
      setCanAddUser(true);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  /**
   * ⭐ New: Validate password confirmation in real-time
   * This provides immediate feedback to the user as they type
   */
  const validatePasswordMatch = (pass, confirm) => {
    if (confirm && pass !== confirm) {
      setPasswordError(t('auth.passwordsNotMatchError'));
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  };

  /**
   * ⭐ Updated: Handle password change with validation
   */
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    // Validate against confirmation if it exists
    if (confirmPassword) {
      validatePasswordMatch(newPassword, confirmPassword);
    }
  };

  /**
   * ⭐ New: Handle confirm password change with validation
   */
  const handleConfirmPasswordChange = (e) => {
    const newConfirm = e.target.value;
    setConfirmPassword(newConfirm);
    validatePasswordMatch(password, newConfirm);
  };

  const validateEmail = (value) => {
    if (!value.trim()) {
      setEmailError('');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError(t('auth.invalidEmailError', 'Please enter a valid email (no spaces, must contain @ and end with a domain)'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value.trim()) validateEmail(value);
  };

  const registerUser = async (e) => {
    e.preventDefault();

    // Validate email format before submission
    if (!validateEmail(email)) {
      notifyError(t('auth.invalidEmailError', 'Please enter a valid email address'));
      return;
    }

    // ⭐ Double-check user limit before submitting
    if (!canAddUser) {
      notifyError(t('tenant.cannotAddUsersError'));
      return;
    }

    // ⭐ New: Validate password confirmation before submission
    if (password !== confirmPassword) {
      setPasswordError(t('auth.passwordsNotMatchError'));
      notifyError(t('auth.passwordsMismatchDetailedError'));
      return;
    }

    // ⭐ New: Validate minimum password length
    if (password.length < 8) {
      setPasswordError(t('auth.passwordTooShortError'));
      notifyError(t('auth.passwordMinLengthErrorDetailed'));
      return;
    }

    setIsSubmitting(true);

    try {
      const data = await registerUserApi(tenant, {
        username,
        email,
        firstname: firstName,
        lastname: lastName,
        phone,
        password,
        role,
      });

      notifySuccess(t('auth.accountCreatedSuccessToast'));

      // ⭐ Update user limit info if provided in response
      if (data.userLimitInfo) {
        setUserLimitInfo({
          currentUsers: data.userLimitInfo.currentUsers,
          maxUsers: data.userLimitInfo.maxUsers,
          message: `${data.userLimitInfo.currentUsers}/${data.userLimitInfo.maxUsers} users created`
        });
      }

      // Clear form
      setUsername('');
      setEmail('');
      setFirstName('');
      setLastName('');
      setPhone('');
      setPassword('');
      setConfirmPassword('');
      setPasswordError('');

      // Navigate back to dashboard
      navigate(`/${tenant}/dashboard`);
    } catch (error) {
      console.error(error);

      // ⭐ Handle specific error cases
      if (error.response?.status === 403) {
        // User limit reached
        const errorData = error.response.data;
        notifyError(errorData.message || t('tenant.userLimitReachedError'));
        setCanAddUser(false);

        if (errorData.details) {
          setUserLimitInfo({
            currentUsers: errorData.details.currentUsers,
            maxUsers: errorData.details.maxUsers,
            message: errorData.message
          });
        }
      } else {
        const msg = error?.response?.data?.message || error.message || t('auth.accountCreationError');
        notifyError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDashboard = () => {
    navigate(`/${tenant}/dashboard`);
  };

  /**
   * ⭐ New function: Handle upgrade plan click
   */
  const handleUpgrade = () => {
    navigate(`/${tenant}/billing/subscription`);
  };

  // Permission gate
  if (!hasAddPermission) {
    return (
      <div className="container" style={{ paddingBottom: '60px' }}>
        <h2 className="hdr hdr--primary hdr--lg">{t('auth.createAccountTitle')}</h2>
        <div className="desktop-action-bar">
          <button type="button" onClick={handleDashboard}>
            <FaHome /> {t('common.home')}
          </button>
        </div>
        <div className="alert alert-danger flex items-center" style={{ gap: "10px" }}>
          <FaLock /> You do not have permission to add users.
        </div>
        <AppFooter buttons={[{ icon: <FaHome />, label: t('common.home'), onClick: handleDashboard }]} />
      </div>
    );
  }

  // ⭐ Show loading state while checking limit
  if (isCheckingLimit) {
    return (
      <div className="container">
        <h2 className="hdr hdr--primary hdr--lg">{t('auth.createAccountTitle')}</h2>
        <div className="text-center p-4">
          <p>{t('tenant.checkingUserLimitText')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBottom: '60px' }}>
      <h2 className="hdr hdr--primary hdr--lg">{t('auth.createAccountTitle')}</h2>

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={handleDashboard} disabled={isSubmitting}>
          <FaHome /> {t('common.home')}
        </button>
        <button
          type="button"
          className="btn-primary-action"
          onClick={registerUser}
          disabled={!canAddUser || isSubmitting || password !== confirmPassword || !!passwordError || !password}
        >
          <FaPlus /> {isSubmitting ? t('common.creatingButton') : t('common.createButton')}
        </button>
      </div>

      {/* ⭐ New: User limit warning banner */}
      {userLimitInfo && userLimitInfo.maxUsers > 0 && (
        <div className={`alert ${canAddUser ? 'alert-info' : 'alert-warning'} mb-3`}>
          <div className="flex items-center">
            {!canAddUser && <FaExclamationTriangle className="mr-2" />}
            <div className="flex-grow">
              <strong>{t('tenant.userLimitLabel')}</strong>
              {userLimitInfo.message}
              {!canAddUser && (
                <p className="mb-0 mt-1">
                  <small>
                    {t('tenant.reachedUserLimitText')}
                    <button
                      onClick={handleUpgrade}
                      className="btn btn-link btn-sm p-0 ml-1"
                      style={{ textDecoration: 'underline' }}
                    >
                      {t('tenant.upgradePlanButton')}
                    </button>
                    {t('tenant.toAddMoreUsersText')}
                  </small>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={registerUser}>
        <div className="form-group">
          <label htmlFor="username">{t('auth.usernameLabel')}</label>
            <input
              className="textInput"
              type="text"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('auth.usernamePlaceholder')}
              required
              disabled={!canAddUser || isSubmitting}
            />
        </div>
        <div className="form-group">
            <label htmlFor="firstName">{t('auth.firstNameLabel')}</label>
            <input
              className="textInput"
              type="text"
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('auth.firstNamePlaceholder')}
              required
              disabled={!canAddUser || isSubmitting}
            />
        </div>
        <div className="form-group">
            <label htmlFor="lastName">{t('auth.lastNameLabel')}</label>
            <input
              className="textInput"
              type="text"
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('auth.lastNamePlaceholder')}
              required
              disabled={!canAddUser || isSubmitting}
            />
        </div>
        <div className="form-group">
            <label htmlFor="email">{t('auth.emailLabel')}</label>
            <input
              className="textInput"
              type="email"
              name="email"
              value={email}
              onChange={handleEmailChange}
              placeholder={t('auth.emailAddress')}
              required
              disabled={!canAddUser || isSubmitting}
            />
            {emailError && <p className="text-red-600">{emailError}</p>}
        </div>
        <div className="form-group">
            <label htmlFor="phone">{t('auth.phoneLabel')}</label>
            <input
              className="textInput"
              type="text"
              name="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('auth.phonePlaceholder')}
              required
              disabled={!canAddUser || isSubmitting}
            />
        </div>
        <div className="form-group">
            <label htmlFor="password">{t('auth.passwordLabel')}</label>
            <PasswordInput
                id="password"
                className="textInput"
                value={password}
                onChange={handlePasswordChange}
                placeholder={t('auth.passwordPlaceholder')}
                required
                minLength={8}
                disabled={!canAddUser || isSubmitting}
            />
            {/* ⭐ New: Show password requirements hint */}
            <small className="form-text text-gray-400">
              {t('auth.passwordRequirementsHint')}
            </small>
        </div>
        {/* ⭐ New: Password confirmation field */}
        <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.confirmPasswordLabel')}</label>
            <PasswordInput
                id="confirmPassword"
                className={`textInput ${passwordError ? 'error' : ''}`}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                placeholder={t('auth.reenterPasswordPlaceholder')}
                required
                minLength={8}
                disabled={!canAddUser || isSubmitting}
            />
            {/* ⭐ New: Display password match error in real-time */}
            {passwordError && (
                <small className="form-text text-red-600">
                    {passwordError}
                </small>
            )}
            {/* ⭐ New: Display success indicator when passwords match */}
            {confirmPassword && !passwordError && password === confirmPassword && (
                <small className="form-text text-green-600">
                    {t('auth.passwordsMatchSuccess')}
                </small>
            )}
        </div>
        <div className="form-group">
          <label>{t('tenant.roleLabel')}</label>
          <select
            className="textInput"
            value={role}
            onChange={(e)=>setRole(e.target.value)}
            disabled={!canAddUser || isSubmitting}
          >
            <option value="admin">{t('tenant.roleAdmin')}</option>
            <option value="manager">{t('tenant.roleManager')}</option>
            <option value="operator">{t('tenant.roleOperator')}</option>
            <option value="contributor">{t('tenant.roleContributor')}</option>
          </select>
        </div>
      </form>

      <AppFooter
        buttons={[
          {
            icon: <FaHome />,
            label: t('common.home'),
            onClick: handleDashboard,
            disabled: isSubmitting
          },
          {
            icon: <FaPlus />,
            label: isSubmitting ? t('common.creatingButton') : t('common.createButton'),
            onClick: registerUser,
            type: 'submit',
            // ⭐ Updated: Also disable if passwords don't match
            disabled: !canAddUser || isSubmitting || password !== confirmPassword || passwordError || !password
          },
        ]}
      />
    </div>
  );
}

export default UserAdd;
